/* eslint-disable complexity */
import { PaymentStatus } from '@futrli-sdk/billing';
import { useErrorHandler, getErrorStatus, ERROR_STATUS } from '@futrli/errors';
import { Organisation } from '@futrli-sdk/organisations';
import {
  getOrganisationById,
  organisationByIdSelector,
  PREDICT_PRODUCT_SLUG,
  isInTrialPeriod,
  findProductSetupById,
  PREDICT_PRODUCT_ID,
  matchPredictSyncingProgressPath,
  isPending,
  matchNewsfeedIntroPath,
  ProductSlug,
  workspaceByIdSelector,
  getOrganisationsSettingsUrl
} from '@futrli/shared-services';
import { Workspace, WorkspacesListingItem } from '@futrli-sdk/workspaces';
import { Location } from 'history';
import { isNull } from 'lodash';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useLocation, useNavigate } from 'react-router-dom';

import { StoreState } from '../core/redux-utils/types';
import { fetchBillingSubscriptionMeta } from '../core/api';
import { PREDICT_TRIAL_PERIOD_HOURS } from '../core/config';
import { UpgradeToProBanner, TrialPeriodTimeUnits } from './upgrade-to-pro-banner';
import { SubscriptionPaymentFailedBanner } from './subscription-payment-failed-banner';
import { useTrialBanner } from '@futrli/predict/hooks/useTrialBanner';
import { useIntegrationsView } from '@futrli/views/hooks/useIntegrationsView';

import styles from './notifications.scss';

const NotificationBannerManagerFC: React.FC = () => {
  const {
    workspaceId: tempWorkspaceId,
    organisationId: tempOrganisationId,
    productSlug: tempProductSlug
  } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisationId || '';
  const paramsProductSlug = tempProductSlug || '';
  const location = useLocation();
  const navigate = useNavigate();
  const handleError = useErrorHandler();
  const [hasSubscriptionPaymentFailed, setHasSubscriptionPaymentFailed] = React.useState<boolean | null>(null);
  const [isCurrentProductPaid, setIsCurrentProductPaid] = React.useState<boolean | null>(null);
  const dispatch = useDispatch();
  const { hasTrialBanner } = useTrialBanner();
  const isOrgLoading = useSelector((state: StoreState) =>
    isPending(state.loadingRequests.FETCH_FROM_REMOTE_GET_ORGANISATION_BY_ID)
  );
  const hideNotificationBanner = shouldHideNotificationBanner(location);
  const productSlug = checkProductRouteBySlug(paramsProductSlug) ? (paramsProductSlug as ProductSlug) : undefined;
  const { integrations } = useIntegrationsView(!hideNotificationBanner && !!productSlug);
  const organisation = useSelector((store: StoreState) =>
    organisationByIdSelector(store.normalised, workspaceId, organisationId)
  );

  const workspace = useSelector(({ normalised }: StoreState) => workspaceByIdSelector(normalised, workspaceId));

  if (workspace?.subscription_state === 'inactive') {
    navigate(getOrganisationsSettingsUrl(workspaceId));
  }

  const loadData = () => {
    if (hideNotificationBanner || !productSlug) {
      return;
    }

    if (!isOrgLoading && !organisation) {
      dispatch(getOrganisationById(workspaceId, organisationId));
    }

  };

  const determinePaidStatus = () => {
    if (!productSlug || hideNotificationBanner || !organisation) {
      return;
    }

    const productSetup = findProductSetupById(organisation, PREDICT_PRODUCT_ID);
    const isPro = productSetup ? productSetup.is_pro : false;

    setIsCurrentProductPaid(isPro);
  };

  useEffect(loadData, [organisation, integrations]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(determinePaidStatus, [organisation]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Determines the status of last subscription payment.
   */
  useEffect(() => {
    if (!isCurrentProductPaid) {
      return;
    }

    const loadBillingMeta = async () => {
      try {
        const subscriptionMeta = await fetchBillingSubscriptionMeta(workspaceId);

        if (subscriptionMeta.last_payment_status === PaymentStatus.failed) {
          return setHasSubscriptionPaymentFailed(true);
        }

        setHasSubscriptionPaymentFailed(false);
      } catch (error) {
        // If 404, we haven't recorded any subscription meta for that user yet, this means
        // the user hasn't paid yet or haven't got any upgraded product.
        if (getErrorStatus(error) === ERROR_STATUS._404) {
          return setHasSubscriptionPaymentFailed(false);
        }

        handleError(error);
      }
    };
    loadBillingMeta();
  }, [workspaceId, isCurrentProductPaid]); // eslint-disable-line react-hooks/exhaustive-deps

  if (
    (!hideNotificationBanner && isNull(isCurrentProductPaid)) ||
    (isCurrentProductPaid && isNull(hasSubscriptionPaymentFailed)) ||
    hideNotificationBanner ||
    !productSlug ||
    !organisation ||
    !integrations
  ) {
    return null;
  }

  if (isCurrentProductPaid && hasSubscriptionPaymentFailed) {
    return (
      <div className={styles.goPro} role="complementary">
        <SubscriptionPaymentFailedBanner productSlug={productSlug} workspaceId={workspaceId} />
      </div>
    );
  }

  const notification = getNotificationBanner(
    productSlug,
    organisation,
    isCurrentProductPaid!,
    hasTrialBanner,
    workspace
  );

  if (!notification) {
    return null;
  }

  return (
    <div className={styles.goPro} style={{ marginBottom: 0 }} role="complementary">
      {notification}
    </div>
  );
};
export const NotificationBannerManager = React.memo(NotificationBannerManagerFC);

/**
 * Gets the specific banner to render in the current organisation product.
 * @param {ProductSlug} productSlug
 * @param {Integration[]} integrations
 * @param {Organisation} organisation
 * @param {LDProps['flags']} flags
 * @param {string} pathname
 * @param {() => void} handleClose
 * @param {boolean} handleClose
 * @returns {JSX.Element} Unpaid product banner.
 */
const getNotificationBanner = (
  productSlug: ProductSlug,
  organisation: Organisation,
  isCurrentProductPaid: boolean,
  hasTrialBanner: boolean,
  workspace: Workspace | WorkspacesListingItem | null
) => {
  const productSetup = findProductSetupById(organisation, PREDICT_PRODUCT_ID);

  if (!productSetup) {
    return null;
  }

  switch (productSlug) {
    case PREDICT_PRODUCT_SLUG: {
      if (workspace && workspace.subscription_state) {
        if (hasTrialBanner) {
          return (
            <UpgradeToProBanner
              productSlug={productSlug}
              productSetup={productSetup}
              trialPeriodTimeInHours={Number(PREDICT_TRIAL_PERIOD_HOURS)}
              formatTrialPeriodTimeUnitsTo={TrialPeriodTimeUnits.days}
            />
          );
        }
        return null;
      } else {
        if (isCurrentProductPaid || !isInTrialPeriod(productSetup, Number(PREDICT_TRIAL_PERIOD_HOURS))) {
          return null;
        }

        return (
          <UpgradeToProBanner
            productSlug={productSlug}
            productSetup={productSetup}
            trialPeriodTimeInHours={Number(PREDICT_TRIAL_PERIOD_HOURS)}
            formatTrialPeriodTimeUnitsTo={TrialPeriodTimeUnits.days}
          />
        );
      }
    }
    default:
      return null;
  }
};

/**
 * Checks by current location path whether the product specific banner has to be hidden
 * or not.
 * @param {Location} location - Current location.
 * @returns {boolean}
 */
const shouldHideNotificationBanner = (location: Location): boolean =>
  !isNull(matchPredictSyncingProgressPath(location.pathname)) || !isNull(matchNewsfeedIntroPath(location.pathname));

/**
 * Checks from the product slug param of the url if it is valid
 * product route to show the notification banner.
 *
 * @param {string} productSlug introduction route
 */
const checkProductRouteBySlug = (productSlug: string) => [PREDICT_PRODUCT_SLUG].includes(productSlug);
